/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import {
  ICarbonScopeDetailWithLoaderContent,
  IDetailComponentProps,
  ICarbonScopeOneData,
  ICarbonScopeOneStackedData
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { Card, Icon, Tooltip } from '@scuf/common';
import CarbonScopeOneStackedDetailsComponent from './CarbonScopeOneStackedDetailsComponent';
import CarbonScopeOneDetailsComponent from './CarbonScopeOneDetailsComponent';
import { ApiContext } from '../../../../context/ApiContext';

const CarbonScopeOneDetailWithLoaderContent = (
  props: ICarbonScopeDetailWithLoaderContent
) => {
  const d = descriptors.energyManagement;
  const e = descriptors.DetailedView;
  const [t] = useTranslation();
  const { data, onRefresh, carbonAllSiteMonthData } = props;
  const emaasContext = useContext(ApiContext);
  const { toggleFeatureScopeOne } = emaasContext!;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div className="carbondetail-performance-detail-container">
      <div>
        {toggleFeatureScopeOne ? (
          <CarbonScopeOneStackedDetailsComponent
            data={data as ICarbonScopeOneStackedData}
            trendChartCategory={t(e.SCOPE1)}
            carbonAllSiteMonthData={carbonAllSiteMonthData}
          />
        ) : (
          <CarbonScopeOneDetailsComponent
            data={data as ICarbonScopeOneData}
            trendChartCategory={t(e.SCOPE1)}
          />
        )}
      </div>
    </div>
  );
};

const CarbonScopeOneDetailWithLoader = withLoadingStatus(
  withErrorStatus(CarbonScopeOneDetailWithLoaderContent)
);

const CarbonScopeOneDetailWidget = (props: IDetailComponentProps) => {
  const [t] = useTranslation();
  const d = descriptors.DetailedView;
  const e = descriptors.energyManagement;
  const { dataConnector, isSite } = props;
  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const carbonScopeOneData = (all as unknown) as ICarbonScopeOneData;
  const isDataAvailable = carbonScopeOneData ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as ICarbonScopeOneData);
  const [carbonAllSiteMonthData, setAllSiteMonthData] = useState(true);

  const checkIfAllSiteHasCompleteData = (data: any) => {
    return (
      isSite ||
      (data &&
        data.breakdownBySite &&
        data.breakdownBySite.every(
          (item: { isCompleted: boolean }) => item.isCompleted
        ))
    );
  };

  const checkIfAllMonthHasCompleteData = (data: any) => {
    return (
      isSite ||
      (data &&
        data.monthWiseAggregations &&
        data.monthWiseAggregations.every(
          (item: { isCompleted: boolean }) => item.isCompleted
        ))
    );
  };

  // Only Applicable for Portfolio
  const checkIfAllSitesHasPreviousPeriod = (data: any) => {
    return (
      isSite ||
      (data &&
        data.breakdownBySite &&
        data.breakdownBySite.every(
          (item: {
            totalPreviousPeriodEmission: number | null;
            totalActualEmission: number | null;
          }) =>
            item.totalActualEmission !== 0 &&
            item.totalPreviousPeriodEmission !== 0 &&
            item.totalActualEmission !== null &&
            item.totalPreviousPeriodEmission !== null
        ))
    );
  };

  // Only Applicable for Portfolio
  const checkIfAllMonthsHasPreviousPeriod = (data: any) => {
    return (
      isSite ||
      (data &&
        data.monthWiseAggregations &&
        data.monthWiseAggregations.every(
          (item: {
            totalPreviousPeriodEmission: number | null;
            totalActualEmission: number | null;
          }) =>
            item.totalPreviousPeriodEmission !== 0 &&
            item.totalPreviousPeriodEmission !== null &&
            item.totalActualEmission !== 0 &&
            item.totalActualEmission !== null
        ))
    );
  };

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setAllSiteMonthData(
          checkIfAllSitesHasPreviousPeriod(carbonScopeOneData) &&
            checkIfAllMonthsHasPreviousPeriod(carbonScopeOneData) &&
            checkIfAllMonthHasCompleteData(carbonScopeOneData) &&
            checkIfAllSiteHasCompleteData(carbonScopeOneData)
        );
        setData(carbonScopeOneData);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  return (
    <>
      <Card className="emaas-performance-detail-card">
        <Card.Header>
          <span className="emaas-summary-card-header">
            {t(d.SCOPEONETITLE)}
          </span>
          <Tooltip
            element={
              <Icon
                root="common"
                name="badge-info"
                exactSize="0.875rem"
                className="emaas-summary-card-header-info-icon"
              />
            }
            event="hover"
            content={t(d.SCOPEONETOOLTIP)}
            position="right center"
            size="small"
          />
        </Card.Header>
        <Card.Content className="detail-summary-card detail-view">
          <CarbonScopeOneDetailWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(e.LOADING)}
            title={t(e.CONNECTIONERRORTITLE)}
            noDataMessage={t(e.NODATAAVAILABLE)}
            connectionErrorMessage={t(e.CONNECTIONERROR)}
            carbonAllSiteMonthData={carbonAllSiteMonthData}
          />
        </Card.Content>
      </Card>
    </>
  );
};
export default CarbonScopeOneDetailWidget;
